import { streetsForCityWire } from '../utils/israelStreets';
import { normalizeVoiceText, removeVoicePhrase } from './matchVoiceSearchCity';

export type VoiceSearchStreetMatch = {
  /** Catalog street name (usually Hebrew, last-name-first). */
  street: string;
  /** Chip/display form, token order following the spoken query when possible. */
  streetDisplay: string;
  phrase: string;
};

const STREET_WORDS = [
  'street',
  'st',
  'road',
  'rd',
  'avenue',
  'ave',
  'blvd',
  'boulevard',
  'lane',
  'drive',
  'רחוב',
  'ברחוב',
];

const HEBREW_TO_LATIN: Record<string, string> = {
  א: 'a',
  ב: 'b',
  ג: 'g',
  ד: 'd',
  ה: 'h',
  ו: 'o',
  ז: 'z',
  ח: 'h',
  ט: 't',
  י: 'y',
  כ: 'k',
  ך: 'k',
  ל: 'l',
  מ: 'm',
  ם: 'm',
  נ: 'n',
  ן: 'n',
  ס: 's',
  ע: 'a',
  פ: 'p',
  ף: 'p',
  צ: 'z',
  ץ: 'z',
  ק: 'k',
  ר: 'r',
  ש: 'sh',
  ת: 't',
};

type StreetIndexEntry = {
  street: string;
  tokens: string[];
  skeletons: string[];
};

const streetIndexCache = new Map<string, StreetIndexEntry[]>();

function latinizeHebrewToken(token: string): string {
  let out = '';
  for (const ch of token) {
    out += HEBREW_TO_LATIN[ch] ?? (/[a-z0-9]/i.test(ch) ? ch.toLowerCase() : '');
  }
  return out;
}

function consonantSkeleton(value: string): string {
  const latin = /[\u0590-\u05ff]/.test(value)
    ? latinizeHebrewToken(value)
    : normalizeVoiceText(value).replace(/[^a-z0-9]/g, '');
  return latin.replace(/[aeiouhwy]/g, '');
}

function tokenizeStreetName(value: string): string[] {
  return normalizeVoiceText(value)
    .replace(/[.,]/g, ' ')
    .split(' ')
    .map((token) => token.trim())
    .filter((token) => token.length > 0 && !STREET_WORDS.includes(token));
}

function stripStreetWords(value: string): string {
  let out = normalizeVoiceText(value);
  for (const word of STREET_WORDS) {
    out = removeVoicePhrase(out, word);
  }
  out = out.replace(/^(on|at)\s+/i, '').replace(/\s+(on|at)$/i, '').trim();
  return out;
}

function stripHebrewArticle(token: string): string {
  const normalized = normalizeVoiceText(token);
  if (/^ה[\u0590-\u05ff]{2,}/.test(normalized)) {
    return normalized.slice(1);
  }
  return normalized;
}

/** CBS often doubles י (שיינקין); speakers usually say שינקין. */
function foldHebrewDoubleYod(token: string): string {
  return token.replace(/יי/g, 'י');
}

/** Spoken “בשינקין / לרחוב …” — strip ב/ל/מ/כ clitics from the query side only. */
function stripHebrewStreetClitic(token: string): string {
  const normalized = foldHebrewDoubleYod(stripHebrewArticle(token));
  if (/^[בלמכ][\u0590-\u05ff]{3,}/.test(normalized)) {
    return foldHebrewDoubleYod(normalized.slice(1));
  }
  return normalized;
}

function tokensMatch(queryToken: string, streetToken: string): boolean {
  const q = stripHebrewStreetClitic(queryToken);
  const s = foldHebrewDoubleYod(stripHebrewArticle(streetToken));
  if (!q || !s) {
    return false;
  }
  if (q === s || foldHebrewDoubleYod(stripHebrewArticle(q)) === s) {
    return q.length >= 2 && s.length >= 2;
  }
  return skeletonsMatch(q, s);
}

function isHebrewToken(value: string): boolean {
  return /[\u0590-\u05ff]/.test(value);
}

function skeletonsMatch(a: string, b: string): boolean {
  const sa = consonantSkeleton(a);
  const sb = consonantSkeleton(b);
  if (!sa || !sb) {
    return normalizeVoiceText(a) === normalizeVoiceText(b);
  }
  if (sa === sb) {
    if (sa.length >= 3) {
      return true;
    }
    // Latin ↔ Hebrew transliteration ("yigal" / "יגאל") can be 2 consonants.
    return sa.length >= 2 && isHebrewToken(a) !== isHebrewToken(b);
  }
  if (sa.length < 3 || sb.length < 3) {
    return false;
  }
  // Avoid “בשינקין” (bshnkn) matching “הבשן” (bshn) via loose prefix.
  const shorter = Math.min(sa.length, sb.length);
  const longer = Math.max(sa.length, sb.length);
  if (longer - shorter > 1 && shorter / longer < 0.8) {
    return false;
  }
  return sa.startsWith(sb) || sb.startsWith(sa);
}

function uniqueMappedTokens(queryTokens: string[], streetTokens: string[]): string[] | null {
  const used = new Set<number>();
  const mapped: string[] = [];
  for (const queryToken of queryTokens) {
    const idx = streetTokens.findIndex((streetToken, i) => !used.has(i) && tokensMatch(queryToken, streetToken));
    if (idx < 0) {
      return null;
    }
    used.add(idx);
    mapped.push(streetTokens[idx]);
  }
  return mapped;
}

function streetIndexForCity(cityHe: string): StreetIndexEntry[] {
  const key = cityHe.trim();
  const cached = streetIndexCache.get(key);
  if (cached) {
    return cached;
  }
  const entries = streetsForCityWire(key).map((street) => {
    const tokens = tokenizeStreetName(street);
    return {
      street,
      tokens,
      skeletons: tokens.map((token) => consonantSkeleton(token)),
    };
  });
  streetIndexCache.set(key, entries);
  return entries;
}

function scoreMatch(queryTokens: string[], entry: StreetIndexEntry): number {
  if (queryTokens.length === 0) {
    return 0;
  }
  const mapped = uniqueMappedTokens(queryTokens, entry.tokens);
  if (!mapped || mapped.length !== queryTokens.length) {
    return 0;
  }
  let score = queryTokens.length * 10;
  if (entry.tokens.length === queryTokens.length) {
    score += 5;
  }
  return score;
}

function extractStreetQuery(text: string): Array<{ query: string; minTokens: number }> {
  const hay = normalizeVoiceText(text);
  if (!hay) {
    return [];
  }
  const out: Array<{ query: string; minTokens: number }> = [];
  const explicit = [
    hay.match(/\bon\s+(.+?)\s+(?:street|st|road|rd|avenue|ave|blvd|boulevard)\b/i)?.[0],
    hay.match(/\b(?:street|st|road|rd|avenue|ave)\s+(.+)/i)?.[0],
    hay.match(/(?:ברחוב|רחוב)\s+(.+)/)?.[0],
  ].filter((value): value is string => Boolean(value));
  for (const value of explicit) {
    const query = stripStreetWords(value);
    if (query) {
      out.push({ query, minTokens: 1 });
    }
  }
  const stripped = stripStreetWords(hay);
  if (stripped) {
    out.push({ query: stripped, minTokens: 2 });
  }
  return out;
}

type BestStreetHit = {
  street: string;
  score: number;
  queryTokens: string[];
  phrase: string;
};

function findBestStreetHit(index: StreetIndexEntry[], text: string): BestStreetHit | null {
  let best: BestStreetHit | null = null;
  const queries = extractStreetQuery(text);
  for (let q = 0; q < queries.length; q += 1) {
    const item = queries[q];
    if (!item) {
      continue;
    }
    const { query, minTokens } = item;
    const tokens = tokenizeStreetName(query);
    if (tokens.length < minTokens) {
      continue;
    }
    const maxWindow = Math.min(4, tokens.length);
    for (let size = maxWindow; size >= minTokens; size -= 1) {
      for (let start = 0; start <= tokens.length - size; start += 1) {
        const windowTokens = tokens.slice(start, start + size);
        for (let i = 0; i < index.length; i += 1) {
          const candidate = index[i];
          if (!candidate) {
            continue;
          }
          const score = scoreMatch(windowTokens, candidate);
          if (score <= 0) {
            continue;
          }
          if (best === null || score > best.score) {
            best = {
              street: candidate.street,
              score,
              queryTokens: windowTokens,
              phrase: windowTokens.join(' '),
            };
          }
        }
        if (best !== null && best.score >= size * 10 + 5) {
          break;
        }
      }
      if (best !== null && best.queryTokens.length >= 2) {
        break;
      }
    }
  }
  return best;
}

function displayStreet(catalogStreet: string, queryTokens: string[]): string {
  const streetTokens = tokenizeStreetName(catalogStreet);
  const mapped = uniqueMappedTokens(queryTokens, streetTokens);
  if (mapped && mapped.length === queryTokens.length && mapped.length >= 2) {
    return mapped.join(' ');
  }
  return catalogStreet;
}

function captureExplicitStreetName(text: string): string | null {
  const queries = extractStreetQuery(text).filter((item) => item.minTokens === 1);
  for (const item of queries) {
    const tokens = tokenizeStreetName(item.query);
    if (tokens.length >= 1 && item.query.trim()) {
      return tokens.join(' ');
    }
  }
  return null;
}

/** Match a spoken street against the Israel street catalog for a city. */
export function matchVoiceSearchStreet(cityHe: string | null, text: string): VoiceSearchStreetMatch | null {
  if (!text.trim()) {
    return null;
  }
  const city = cityHe?.trim() || '';
  if (city) {
    const index = streetIndexForCity(city);
    if (index.length > 0) {
      const hit = findBestStreetHit(index, text);
      if (hit !== null) {
        return {
          street: hit.street,
          streetDisplay: displayStreet(hit.street, hit.queryTokens),
          phrase: hit.phrase,
        };
      }
      const stripped = stripStreetWords(normalizeVoiceText(text));
      const tokens = tokenizeStreetName(stripped);
      if (tokens.length === 1) {
        const token = tokens[0] ?? '';
        const exact = index.find(
          (entry) => entry.tokens.length === 1 && tokensMatch(token, entry.tokens[0] ?? ''),
        );
        if (exact && token) {
          return {
            street: exact.street,
            streetDisplay: exact.street,
            phrase: token,
          };
        }
      }
    }
  }
  const explicit = captureExplicitStreetName(text);
  if (!explicit || explicit.length < 2) {
    return null;
  }
  // Without a city, a long leftover after “ברחוב” is often “street + mistyped city”
  // (“יגאל אלון בל אביב”). Do not hard-filter on that — let keyword/venue search run.
  const explicitTokens = tokenizeStreetName(explicit);
  if (explicitTokens.length > 2) {
    return null;
  }
  return {
    street: explicit,
    streetDisplay: explicit,
    phrase: explicit,
  };
}

export function stripStreetMatch(text: string, match: VoiceSearchStreetMatch): string {
  let out = removeVoicePhrase(text, match.phrase);
  out = stripStreetWords(out);
  return out.replace(/^(on|at)\s+/i, '').trim();
}
